import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera,
  CheckCircle2,
  Clock3,
  Loader2,
  LocateFixed,
  LogIn,
  LogOut,
  MapPin,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  X,
} from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, { PageHead, useAdmin } from "@/components/AdminShell";
import { ADMIN_BASE } from "@/lib/config";
import { Link } from "react-router-dom";

function getLocation() {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Perangkat/browser tidak mendukung GPS."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }),
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  });
}

function minutesText(value) {
  const minutes = Number(value || 0);
  if (!minutes) return "-";
  return `${Math.floor(minutes / 60)}j ${minutes % 60}m`;
}

function AttendanceInner() {
  const admin = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [action, setAction] = useState("check_in");
  const [photoData, setPhotoData] = useState("");
  const [location, setLocation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const isOwner = admin.user?.role === "owner";

  async function load() {
    if (isOwner) {
      setLoading(false);
      return;
    }

    try {
      const { data: response } = await http.get("/admin/attendance/me?limit=30");
      setData(response);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    return () => stopCamera();
  }, [isOwner]);

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  async function openCamera(nextAction) {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Browser ini tidak mendukung kamera live.");
      return;
    }

    setAction(nextAction);
    setPhotoData("");
    setLocation(null);
    setCameraOpen(true);
    setCameraBusy(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      toast.error("Kamera tidak dapat dibuka. Izinkan akses kamera lalu coba lagi.");
      setCameraOpen(false);
      stopCamera();
    } finally {
      setCameraBusy(false);
    }
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      toast.error("Kamera belum siap.");
      return;
    }

    const maxWidth = 960;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhotoData(canvas.toDataURL("image/jpeg", 0.82));

    try {
      const coords = await getLocation();
      setLocation(coords);
      toast.success("Selfie dan lokasi berhasil diambil.");
    } catch (e) {
      toast.error(e?.message || "Lokasi GPS tidak dapat diambil.");
    }
  }

  async function submitAttendance() {
    if (!photoData) {
      toast.error("Ambil selfie terlebih dahulu.");
      return;
    }

    setBusy(true);

    try {
      let coords = location;
      if (!coords) coords = await getLocation();

      const endpoint =
        action === "check_in"
          ? "/admin/attendance/check-in"
          : "/admin/attendance/check-out";

      await http.post(endpoint, {
        photo_data: photoData,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
      });

      toast.success(action === "check_in" ? "Absen masuk berhasil." : "Absen pulang berhasil.");
      stopCamera();
      setCameraOpen(false);
      setPhotoData("");
      setLocation(null);
      setLoading(true);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (isOwner) {
    return (
      <div className="rounded-3xl border border-border bg-card p-8 text-center">
        <ShieldCheck className="mx-auto text-primary" size={36} />
        <h2 className="mt-3 font-heading text-xl font-extrabold">Menu Pemilik Terpisah</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Pemilik tidak melakukan Check-in/Check-out dari halaman ini.
        </p>
        <Link
          to={`${ADMIN_BASE}/attendance-admin`}
          className="mt-4 inline-flex rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground"
        >
          Buka Absensi Karyawan
        </Link>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="flex justify-center pt-20"><Loader2 className="wm-spin text-muted-foreground" /></div>;
  }

  const today = data.today;
  const checkedIn = Boolean(today?.check_in_at);
  const checkedOut = Boolean(today?.check_out_at);

  return (
    <>
      <PageHead
        eyebrow="Karyawan"
        title="Absensi"
        desc="Selfie dan koordinat GPS wajib dicatat saat masuk dan pulang."
      >
        <button onClick={load} className="grid h-10 w-10 place-items-center rounded-xl bg-muted">
          <RefreshCw size={16} />
        </button>
      </PageHead>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-3xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Waktu Server</div>
          <div className="mt-2 font-heading text-lg font-extrabold">{data.server_time}</div>
          <div className="mt-1 text-xs text-muted-foreground">Zona: {data.settings?.timezone}</div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Masuk</div>
          <div className="mt-2 font-heading text-2xl font-extrabold">{today?.check_in_time || "--:--:--"}</div>
          <div className={`mt-1 text-xs font-bold ${today?.late_minutes > 0 ? "text-amber-500" : "text-emerald-500"}`}>
            {checkedIn ? (today?.late_minutes > 0 ? `Telat ${today.late_minutes} menit` : "Tepat waktu") : "Belum absen masuk"}
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Pulang</div>
          <div className="mt-2 font-heading text-2xl font-extrabold">{today?.check_out_time || "--:--:--"}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {checkedOut ? `Durasi ${minutesText(today?.work_minutes)}` : checkedIn ? "Belum absen pulang" : "Menunggu absen masuk"}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Camera size={18} /></div>
          <div>
            <b className="font-heading text-lg">Absensi Hari Ini</b>
            <div className="text-xs text-muted-foreground">
              Shift {data.settings?.shift_start} - {data.settings?.shift_end} · Radius {data.settings?.radius_meters} m
            </div>
          </div>
        </div>

        {!checkedIn ? (
          <button
            onClick={() => openCamera("check_in")}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-bold text-primary-foreground"
          >
            <LogIn size={18} /> Absen Masuk
          </button>
        ) : !checkedOut ? (
          <button
            onClick={() => openCamera("check_out")}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-bold text-primary-foreground"
          >
            <LogOut size={18} /> Absen Pulang
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-500/10 py-4 font-bold text-emerald-500">
            <CheckCircle2 size={18} /> Absensi hari ini selesai
          </div>
        )}
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-5">
        <h2 className="font-heading text-lg font-extrabold">Riwayat Saya</h2>
        <div className="mt-3 space-y-2">
          {(data.history || []).map((row) => (
            <div key={row.id} className="rounded-2xl bg-muted p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <b className="text-sm">{row.date}</b>
                <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${row.late_minutes > 0 ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"}`}>
                  {row.late_minutes > 0 ? `Telat ${row.late_minutes}m` : "Hadir"}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>Masuk <b className="text-card-foreground">{row.check_in_time || "-"}</b></div>
                <div>Pulang <b className="text-card-foreground">{row.check_out_time || "-"}</b></div>
              </div>
            </div>
          ))}
          {(data.history || []).length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Belum ada riwayat absensi.</div>
          )}
        </div>
      </div>

      {cameraOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-border bg-card shadow-2xl">
            <div className="flex items-center justify-between p-4">
              <div>
                <b className="font-heading text-lg">{action === "check_in" ? "Selfie Absen Masuk" : "Selfie Absen Pulang"}</b>
                <div className="text-xs text-muted-foreground">Foto harus diambil langsung dari kamera.</div>
              </div>
              <button onClick={() => { stopCamera(); setCameraOpen(false); }} className="grid h-9 w-9 place-items-center rounded-xl bg-muted"><X size={17} /></button>
            </div>

            <div className="relative aspect-square bg-black">
              {!photoData ? (
                <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover [transform:scaleX(-1)]" />
              ) : (
                <img src={photoData} alt="Preview selfie" className="h-full w-full object-cover" />
              )}
              {cameraBusy && <div className="absolute inset-0 grid place-items-center bg-black/60"><Loader2 className="wm-spin text-white" /></div>}
            </div>

            <div className="p-4">
              {location && (
                <div className="mb-3 rounded-2xl bg-muted p-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-primary" /><b className="text-card-foreground">Lokasi siap</b></div>
                  <div className="mt-1">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)} · Akurasi ±{Math.round(location.accuracy || 0)} m</div>
                </div>
              )}

              {!photoData ? (
                <button onClick={capturePhoto} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground">
                  <Camera size={18} /> Ambil Selfie
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { setPhotoData(""); setLocation(null); }} className="flex items-center justify-center gap-2 rounded-2xl bg-muted py-3.5 font-bold">
                    <RotateCcw size={17} /> Ulangi
                  </button>
                  <button disabled={busy} onClick={submitAttendance} className="flex items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground disabled:opacity-50">
                    {busy ? <Loader2 className="wm-spin" size={17} /> : <LocateFixed size={17} />}
                    {busy ? "Mengirim..." : "Kirim Absensi"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Attendance() {
  return <AdminShell><AttendanceInner /></AdminShell>;
}
